// Layout primitives for the report pages. The grid rules live in styles.css
// (.rpt-*) so the column counts can change per breakpoint - inline styles can't
// hold media queries, and fixed column counts read far better than `auto-fit`.
import { Button, Segmented } from 'framework7-react';
import type { CSSProperties, ReactNode } from 'react';

const MUTED = '#8e8e93';

/** Centred page column: everything on a report page goes inside one. */
export function ReportsShell({ children }: { children: ReactNode }) {
  return (
    <div className="rpt-shell">
      <div className="rpt-stack">{children}</div>
    </div>
  );
}

/**
 * Segment picker for the report domains.
 *
 * Uses F7's own <Segmented>, not hand-rolled chips: F7 sets a global
 * `button { width: 100% }` and <Segmented> is what makes that work, pairing it
 * with `flex-shrink: 1; min-width: 0` so the buttons divide the strip evenly.
 * A custom strip has to fight that rule; this one is built on it.
 */
export function ReportTabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: T; label: string }[];
  value: T;
  onChange: (key: T) => void;
}) {
  return (
    <Segmented strong>
      {tabs.map((tab) => (
        <Button key={tab.key} active={tab.key === value} onClick={() => onChange(tab.key)}>
          {tab.label}
        </Button>
      ))}
    </Segmented>
  );
}

export function ReportCard({
  title,
  action,
  className,
  children,
}: {
  title?: string;
  action?: ReactNode;
  /** Grid placement from the parent row (rpt-main / rpt-wide). */
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--f7-card-bg-color,#fff)',
        borderRadius: 14,
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      {title && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 16px 0',
          }}
        >
          <span
            style={{
              color: MUTED,
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {title}
          </span>
          {action}
        </div>
      )}
      <div style={{ padding: '12px 16px 14px' }}>{children}</div>
    </div>
  );
}

/** A label/value row inside a ReportCard, with a hairline between siblings. */
export function ReportRow({
  label,
  sublabel,
  value,
  subvalue,
  style,
}: {
  label: string;
  sublabel?: string;
  value: string;
  subvalue?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        padding: '9px 0',
        borderTop: '1px solid rgba(142,142,147,0.18)',
        ...style,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </div>
        {sublabel && <div style={{ color: MUTED, fontSize: 12 }}>{sublabel}</div>}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{value}</div>
        {subvalue && <div style={{ color: MUTED, fontSize: 12 }}>{subvalue}</div>}
      </div>
    </div>
  );
}

/** Navigation row inside a ReportCard - replaces an F7 inset List so the
 *  rounded-card styling is identical on both themes. */
export function ReportNavRow({
  icon,
  color,
  title,
  href,
  first,
}: {
  icon: string;
  color: string;
  title: string;
  href: string;
  first?: boolean;
}) {
  return (
    <a
      href={href}
      className="link"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderTop: first ? 'none' : '1px solid rgba(142,142,147,0.18)',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <span
        style={{
          width: 29,
          height: 29,
          borderRadius: 7,
          background: color,
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className="f7-icons" style={{ fontSize: 17 }}>
          {icon}
        </i>
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 15 }}>{title}</span>
      <i className="f7-icons" style={{ fontSize: 15, color: '#c7c7cc', flexShrink: 0 }}>
        chevron_right
      </i>
    </a>
  );
}
